/**
 * Gamepad API source (standard Xbox mapping) — docs/12 K1 scheme:
 * Left stick / D-pad move, right stick aim+fire, RT & LT (and A) dash,
 * RB supershot, LB slow, B secondary (reroll), X scrap, Start pause.
 * Side dodges stay on the keyboard (Q/E).
 * See docs/06-controls-platforms.md
 */
const DEADZONE = 0.15;

const BTN = {
  a: 0,
  b: 1,
  x: 2,
  y: 3,
  lb: 4,
  rb: 5,
  lt: 6,
  rt: 7,
  start: 9,
  dUp: 12,
  dDown: 13,
  dLeft: 14,
  dRight: 15,
} as const;

export class GamepadSource {
  private prevButtons: boolean[] = [];
  lastActivity = 0;

  /** Dual-rumble haptics where supported (Xbox pads) */
  rumble(strong: number, weak: number, durationMs: number): void {
    const gp = this.pad();
    gp?.vibrationActuator
      ?.playEffect('dual-rumble', {
        duration: durationMs,
        strongMagnitude: strong,
        weakMagnitude: weak,
      })
      .catch(() => {
        /* unsupported — ignore */
      });
  }

  private pad(): Gamepad | null {
    for (const gp of navigator.getGamepads()) {
      if (gp?.connected) return gp;
    }
    return null;
  }

  /** Polled every frame; returns null when no pad is connected */
  read(): GamepadState | null {
    const gp = this.pad();
    if (!gp) return null;

    const axis = (i: number): number => {
      const v = gp.axes[i] ?? 0;
      return Math.abs(v) < DEADZONE ? 0 : v;
    };
    const btn = (i: number): boolean => (gp.buttons[i]?.value ?? 0) > 0.4;
    const pressed = (i: number): boolean => btn(i) && !(this.prevButtons[i] ?? false);

    // D-pad doubles as digital movement — menus stay navigable even if a
    // pad reports flaky stick axes (docs/12 K1)
    const dpadX = (btn(BTN.dRight) ? 1 : 0) - (btn(BTN.dLeft) ? 1 : 0);
    const dpadY = (btn(BTN.dDown) ? 1 : 0) - (btn(BTN.dUp) ? 1 : 0);

    const state: GamepadState = {
      moveX: Math.max(-1, Math.min(1, axis(0) + dpadX)),
      moveY: Math.max(-1, Math.min(1, axis(1) + dpadY)),
      aimX: axis(2),
      aimY: axis(3),
      fire: Math.hypot(axis(2), axis(3)) > 0.3, // fire while right stick is pushed
      supershot: btn(BTN.rb),
      // A keeps confirming menus; RT/LT are the combat dash triggers
      dashPressed: pressed(BTN.a) || pressed(BTN.rt) || pressed(BTN.lt),
      confirmHeld: btn(BTN.a),
      dodgeLeftPressed: false, // keyboard-only (Q/E)
      dodgeRightPressed: false,
      altPressed: pressed(BTN.b),
      trashPressed: pressed(BTN.x),
      infoPressed: pressed(BTN.y),
      slowHeld: btn(BTN.lb),
      pausePressed: pressed(BTN.start),
    };

    this.prevButtons = gp.buttons.map((b) => b.value > 0.4);

    const active =
      state.fire ||
      Math.hypot(state.moveX, state.moveY) > 0 ||
      state.supershot ||
      state.dashPressed ||
      state.dodgeLeftPressed ||
      state.dodgeRightPressed;
    if (active) this.lastActivity = performance.now();

    return state;
  }
}

export interface GamepadState {
  moveX: number;
  moveY: number;
  aimX: number;
  aimY: number;
  fire: boolean;
  supershot: boolean;
  dashPressed: boolean;
  confirmHeld: boolean;
  dodgeLeftPressed: boolean;
  dodgeRightPressed: boolean;
  altPressed: boolean;
  trashPressed: boolean;
  infoPressed: boolean;
  slowHeld: boolean;
  pausePressed: boolean;
}
